/**
 * HTTP service for the migration feature (PX-W7.5, TASK-501F9B15): one
 * service, six route families — plans, runs, reconciliation, go/no-go,
 * rollback and trail. Success bodies are BARE (`JsonResults.ok`, no
 * envelope) — no `response.data.items` unwrapping anywhere.
 *
 * Exactly the seven writes plan Decision 1 named, and no other method (fix
 * cycle 3, GRP-B04710BD: the eighth, `generateReconciliation`, was dead —
 * no caller ever dispatched it — and is removed, along with its thunk,
 * extraReducers and `GenerateReconciliationInput` type): the
 * six plan-authoring writes (`scope`, `cutover-window`, `rollback-path`,
 * `mark-tested`, `owner`, `status`) and the three run writes (`create`,
 * `counts`, `finish`) are a separate deliverable (the plan-authoring /
 * run-operations forms), out of scope here — a service method with no caller
 * is dead code a reviewer would flag.
 */

import { apiClient } from '@/services'
import type {
  AssignDifferenceOwnerInput,
  AuditPage,
  ActivityRow,
  ConfirmGuardrailsInput,
  CreateMigrationPlanInput,
  DisposeDifferenceInput,
  ExecuteRollbackInput,
  GoNoGoRecord,
  GuardrailStatus,
  MigrationPlanRow,
  MigrationRunRow,
  MigrationTrailSummary,
  ProposeDifferenceInput,
  ReconciliationReport,
  ReconciliationTraceEntry,
  RecordGoNoGoInput,
  RollbackStateView
} from '../types'

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}`

export const migrationService = {
  /** Every plan on the project (`GET .../migration-plans`). */
  async listPlans(orgId: string, projectId: string): Promise<MigrationPlanRow[]> {
    return apiClient.get<MigrationPlanRow[]>(`${base(orgId, projectId)}/migration-plans`)
  },

  /** AC-1's create-plan action on an empty project (`POST .../migration-plans`, 201). */
  async createPlan(
    orgId: string,
    projectId: string,
    input: CreateMigrationPlanInput
  ): Promise<MigrationPlanRow> {
    return apiClient.post<MigrationPlanRow>(`${base(orgId, projectId)}/migration-plans`, input)
  },

  /** One plan (`GET .../migration-plans/:planId`). */
  async getPlan(orgId: string, projectId: string, planId: string): Promise<MigrationPlanRow> {
    return apiClient.get<MigrationPlanRow>(`${base(orgId, projectId)}/migration-plans/${planId}`)
  },

  /** The selected plan's runs (`GET .../migration-plans/:planId/runs`). */
  async listRuns(orgId: string, projectId: string, planId: string): Promise<MigrationRunRow[]> {
    return apiClient.get<MigrationRunRow[]>(`${base(orgId, projectId)}/migration-plans/${planId}/runs`)
  },

  /** One run (`GET .../runs/:runId`). Read-only — no run-operations writes here. */
  async getRun(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string
  ): Promise<MigrationRunRow> {
    return apiClient.get<MigrationRunRow>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}`
    )
  },

  /** The ACTIVE reconciliation report for a run (`GET .../reconciliation`). */
  async getReconciliation(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string
  ): Promise<ReconciliationReport> {
    return apiClient.get<ReconciliationReport>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/reconciliation`
    )
  },

  /**
   * Name a candidate owner for one flagged difference — the FIRST of three
   * distinct reconciliation verbs (Decision 9 / INV-5.6). Nested under
   * `runs/:runId/reconciliation/differences/:diffId`, NOT at plan level.
   * @returns The refreshed report — it replaces the loaded one, never a
   *          patch to a single row.
   */
  async assignDifferenceOwner(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string,
    diffId: string,
    input: AssignDifferenceOwnerInput
  ): Promise<ReconciliationReport> {
    return apiClient.post<ReconciliationReport>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/reconciliation/differences/${diffId}/assign-owner`,
      input
    )
  },

  /**
   * Suggest a disposition — the SECOND of three distinct verbs. A proposal
   * can only ever reach `PROPOSED`; it is never a decision.
   * @returns The refreshed report.
   */
  async proposeDifference(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string,
    diffId: string,
    input: ProposeDifferenceInput
  ): Promise<ReconciliationReport> {
    return apiClient.post<ReconciliationReport>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/reconciliation/differences/${diffId}/propose`,
      input
    )
  },

  /**
   * Close a difference out — the THIRD, terminal verb. No `ownerId` field:
   * authorisation reads the persisted owner, never a wire-supplied one.
   * @returns The refreshed report.
   */
  async disposeDifference(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string,
    diffId: string,
    input: DisposeDifferenceInput
  ): Promise<ReconciliationReport> {
    return apiClient.post<ReconciliationReport>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/reconciliation/differences/${diffId}/dispose`,
      input
    )
  },

  /**
   * The CURRENT go/no-go record (`GET .../go-no-go`, current record only —
   * Decision 8; the `?history=true` arm is unspecified and not wired here).
   * 404 means no decision has been taken yet, a different fact from a
   * recorded NO_GO.
   */
  async getGoNoGo(orgId: string, projectId: string, planId: string): Promise<GoNoGoRecord> {
    return apiClient.get<GoNoGoRecord>(`${base(orgId, projectId)}/migration-plans/${planId}/go-no-go`)
  },

  /**
   * Record the written go/no-go (`POST .../go-no-go`, 201) — CLIENT-side
   * only, at any access level including READ (Decision 7); the panel offers
   * this act to a client-side caller only, but the server's own 403 is what
   * gates it.
   */
  async recordGoNoGo(
    orgId: string,
    projectId: string,
    planId: string,
    input: RecordGoNoGoInput
  ): Promise<GoNoGoRecord> {
    return apiClient.post<GoNoGoRecord>(
      `${base(orgId, projectId)}/migration-plans/${planId}/go-no-go`,
      input
    )
  },

  /** A run's rollback window (`GET .../runs/:runId/rollback`). */
  async getRollback(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string
  ): Promise<RollbackStateView> {
    return apiClient.get<RollbackStateView>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/rollback`
    )
  },

  /**
   * Invoke the rollback (`POST .../rollback/execute`) — FIRM-side, on the
   * project's write roster, and only while the server's status allows it;
   * the UI hides, the server decides (Decision 6). There is no release
   * action: no `/rollback/release` route exists.
   */
  async executeRollback(
    orgId: string,
    projectId: string,
    planId: string,
    runId: string,
    input: ExecuteRollbackInput
  ): Promise<RollbackStateView> {
    return apiClient.post<RollbackStateView>(
      `${base(orgId, projectId)}/migration-plans/${planId}/runs/${runId}/rollback/execute`,
      input
    )
  },

  /** The plan's stitched Wave-5 surround at a glance (`GET .../reuse-surround`). */
  async getTrailSummary(
    orgId: string,
    projectId: string,
    planId: string
  ): Promise<MigrationTrailSummary> {
    return apiClient.get<MigrationTrailSummary>(
      `${base(orgId, projectId)}/migration-plans/${planId}/reuse-surround`
    )
  },

  /**
   * One page of `px_audit_entry` rows, newest-first
   * (`GET .../reuse-surround/audit-trail`), the `phaseGateService.listGates`
   * query-building idiom — `limit` / `cursor` omitted when undefined.
   */
  async getAuditTrail(
    orgId: string,
    projectId: string,
    planId: string,
    paging: { limit?: number; cursor?: string } = {}
  ): Promise<AuditPage> {
    const qs = new URLSearchParams()
    if (paging.limit !== undefined) qs.set('limit', String(paging.limit))
    if (paging.cursor !== undefined) qs.set('cursor', paging.cursor)
    const query = qs.toString()
    return apiClient.get<AuditPage>(
      `${base(orgId, projectId)}/migration-plans/${planId}/reuse-surround/audit-trail${query ? `?${query}` : ''}`
    )
  },

  /** The persisted activity rows tagged with this plan, oldest-first (`GET .../activity-trail`). */
  async getActivityTrail(orgId: string, projectId: string, planId: string): Promise<ActivityRow[]> {
    return apiClient.get<ActivityRow[]>(
      `${base(orgId, projectId)}/migration-plans/${planId}/reuse-surround/activity-trail`
    )
  },

  /** The per-difference propose/dispose trace (`GET .../reconciliation-trace`), possibly empty. */
  async getReconciliationTrace(
    orgId: string,
    projectId: string,
    planId: string
  ): Promise<ReconciliationTraceEntry[]> {
    return apiClient.get<ReconciliationTraceEntry[]>(
      `${base(orgId, projectId)}/migration-plans/${planId}/reuse-surround/reconciliation-trace`
    )
  },

  /**
   * Attest that the connector guardrails read as enforced right now
   * (`POST .../guardrails/confirm`, 201). FIRM-only server-side; refused 409
   * when either runtime-observable flag reads false — nothing is written on
   * refusal.
   */
  async confirmGuardrails(
    orgId: string,
    projectId: string,
    planId: string,
    input: ConfirmGuardrailsInput = {}
  ): Promise<GuardrailStatus> {
    return apiClient.post<GuardrailStatus>(
      `${base(orgId, projectId)}/migration-plans/${planId}/reuse-surround/guardrails/confirm`,
      input
    )
  }
}
